/**
 * Footer newsletter (SMS for US, email elsewhere).
 *
 * Formatting, validation and both subscribe calls live in `sms-signup.js`
 * (`window.BWW.smsSignup`), shared with the loyalty "ways to earn" signup popups so the two
 * flows cannot drift. This file owns only the footer's own DOM: forms, messages, button state.
 */
document.addEventListener("DOMContentLoaded", () => {
  const signup = window.BWW?.smsSignup;
  if (!signup) {
    console.error("[Footer] sms-signup.js did not load — newsletter forms are inert");
    return;
  }

  // SMS Newsletter Form (US users)
  const smsForm = document.querySelector("#sms-newsletter-form");
  const phoneInput = document.querySelector("#phone-input");

  // Email Newsletter Form (Non-US users)
  const emailForm = document.querySelector("#email-newsletter-form");
  const emailInput = document.querySelector("#email-input");

  // Shared Newsletter Messages
  const newsletterSuccessMessage = document.getElementById("newsletter-success");
  const newsletterErrorMessage = document.getElementById("newsletter-error");
  const newsletterErrorText = document.getElementById("newsletter-error-text");

  // Initialize SMS form if it exists
  if (smsForm && phoneInput) {
    initializeSMSForm();
  }

  // Initialize Email form if it exists
  if (emailForm && emailInput) {
    initializeEmailForm();
  }

  function initializeSMSForm() {
    // Remove any existing onsubmit attributes
    smsForm.removeAttribute("onsubmit");

    // Progressive `(123) 456-7890` formatting + digit-only entry.
    signup.attachPhoneFormatting(phoneInput);

    // Form submission
    smsForm.addEventListener("submit", handleSMSSubmit);

    phoneInput.addEventListener("input", clearErrors);
  }

  function initializeEmailForm() {
    emailForm.addEventListener("submit", handleEmailSubmit);

    emailInput.addEventListener("input", clearErrors);
  }

  async function handleSMSSubmit(event) {
    event.preventDefault();

    const phone = phoneInput.value.trim();
    const cleanPhone = signup.digitsOnly(phone);

    if (!phone) {
      showError("Please enter a phone number.", phoneInput);
      return;
    }

    if (!signup.isValidPhone(cleanPhone)) {
      showError("Please enter a valid 10-digit phone number.", phoneInput);
      return;
    }

    // Post to the back-end proxy (sms_subscribe_endpoint theme setting). The proxy
    // holds the Postscript API key server-side and forwards to Postscript with the
    // numeric Keyword ID — no credentials are ever sent from the browser.
    // NOTE: read the attribute, not smsForm.action — with no action attribute the
    // property resolves to the current page URL, which would post to the wrong place.
    const endpoint = smsForm.getAttribute("action");
    if (!endpoint) {
      console.warn(
        "[Footer SMS] sms_subscribe_endpoint theme setting is not configured — see scripts/bww-middleware/README.md"
      );
      showError("Unable to subscribe at this time, try again later.", phoneInput);
      return;
    }

    // API v2 keyword ID for JOINBRUNT (from GET /api/v2/keywords). The dashboard URL shows
    // 397454, but the API rejects that internal ID with "Not Found" — it requires this kw_…
    // identifier. Browser-visible by design; it is not a credential.
    const keywordId = smsForm.dataset.keywordId || "kw_2f2c13fa3003a48d"; // gitleaks:allow

    const { ok } = await signup.subscribePhone({ endpoint, phone: cleanPhone, keywordId });
    if (ok) {
      showSuccess("Thanks for subscribing!<br>We'll send you a message shortly.");
    } else {
      showError("Unable to subscribe at this time, try again later.", phoneInput);
    }
  }

  async function handleEmailSubmit(event) {
    event.preventDefault();

    const email = emailInput.value.trim();

    if (!email) {
      showError("Please enter an email address.", emailInput);
      return;
    }

    if (!signup.isValidEmail(email)) {
      showError("Please enter a valid email address.", emailInput);
      return;
    }

    const submitBtn = emailForm.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.innerHTML = "Signing&nbsp;Up...";
    submitBtn.disabled = true;

    // Post to the BWW middleware (form action). It holds the Klaviyo list id
    // server-side and rate-limits the endpoint, so the browser sends only the
    // email address. Read the attribute, not emailForm.action — with no action
    // attribute the property resolves to the current page URL.
    const endpoint = emailForm.getAttribute("action");
    if (!endpoint) {
      console.warn(
        "[Footer email] middleware_base_url theme setting is not configured — see scripts/bww-middleware/README.md"
      );
      showError("Something went wrong. Please try again.", emailInput);
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
      return;
    }

    try {
      const { ok, message } = await signup.subscribeEmail({ endpoint, email });
      if (ok) {
        showSuccess("Thanks for subscribing!<br>We'll send you a message shortly.");
        emailForm.reset();
      } else {
        showError(message || "Something went wrong. Please try again.", emailInput);
      }
    } finally {
      submitBtn.textContent = originalText;
      submitBtn.disabled = false;
    }
  }

  function showError(message, inputElement) {
    if (newsletterErrorMessage && newsletterErrorText) {
      newsletterSuccessMessage.classList.add("hidden");
      newsletterErrorText.textContent = "";
      newsletterErrorMessage.classList.remove("hidden");
      newsletterErrorMessage.classList.add("flex");

      requestAnimationFrame(() => {
        newsletterErrorText.textContent = message;
        if (inputElement) {
          inputElement.setAttribute("aria-invalid", "true");
          inputElement.focus();
        }
      });
    }
  }

  function showSuccess(message) {
    if (newsletterSuccessMessage) {
      newsletterSuccessMessage.innerHTML = message;
      newsletterSuccessMessage.classList.remove("hidden");
      newsletterErrorMessage.classList.remove("flex");
      newsletterErrorMessage.classList.add("hidden");
    }
    if (smsForm) smsForm.classList.add("hidden");
    if (emailForm) emailForm.classList.add("hidden");
    clearErrors();
  }

  function clearErrors() {
    if (newsletterErrorMessage) {
      newsletterErrorMessage.classList.add("hidden");
      newsletterErrorMessage.classList.remove("flex");
    }
    phoneInput?.removeAttribute("aria-invalid");
    emailInput?.removeAttribute("aria-invalid");
  }
});
